import { Calendar, CheckCircle2, Link2, Pencil, User } from 'lucide-react'
import type { ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import { Badge } from '@/components/ui/badge'
import { Card, CardContent } from '@/components/ui/card'
import type { IdentityDetail } from '@/features/identities/types'
import { ContactField } from './ContactField'
import { EditableField } from './EditableField'
import { EditableName } from './EditableName'
import { IdentityAvatar } from './IdentityAvatar'
import { identityDisplayName } from './identity-display-name'

function formatRelativeTime(iso: string | null | undefined): string | null {
  if (!iso) return null
  const ts = Date.parse(iso)
  if (Number.isNaN(ts)) return null
  const diffSec = Math.round((ts - Date.now()) / 1000)
  const rtf = new Intl.RelativeTimeFormat('en', { numeric: 'auto' })
  if (Math.abs(diffSec) < 60) return rtf.format(diffSec, 'second')
  const diffMin = Math.round(diffSec / 60)
  if (Math.abs(diffMin) < 60) return rtf.format(diffMin, 'minute')
  const diffHr = Math.round(diffMin / 60)
  if (Math.abs(diffHr) < 24) return rtf.format(diffHr, 'hour')
  const diffDay = Math.round(diffHr / 24)
  if (Math.abs(diffDay) < 30) return rtf.format(diffDay, 'day')
  const diffMon = Math.round(diffDay / 30)
  if (Math.abs(diffMon) < 12) return rtf.format(diffMon, 'month')
  return rtf.format(Math.round(diffMon / 12), 'year')
}

export interface IdentityHeaderCardProps {
  identity: IdentityDetail
  hasVerifiedDocument: boolean
  onUpdateField: (field: string, next: string) => Promise<void>
  /** The header's buttons (watch, OSINT, PDF …), in the order given. */
  actions?: ReactNode
}

/** Identity anchor with the most-used personal and contact details always in view. */
export function IdentityHeaderCard({
  identity,
  hasVerifiedDocument,
  onUpdateField,
  actions,
}: IdentityHeaderCardProps) {
  const { t } = useTranslation()
  return (
    <Card className="py-0">
      <CardContent className="p-4 sm:p-5">
        <div className="flex flex-wrap items-center gap-4 sm:gap-5">
          <div className="relative size-16 shrink-0 overflow-hidden rounded-2xl border border-border bg-muted sm:size-20">
            <IdentityAvatar
              photoPath={identity.photoPath}
              name={identityDisplayName(identity, t)}
            />
          </div>
          <div className="min-w-0 flex-1 space-y-1">
            <p className="text-2xs font-medium uppercase tracking-wider text-muted-foreground">
              {t('identity.detail.profile')}
            </p>
            <div className="flex flex-wrap items-center gap-3">
              <EditableName
                value={identity.name}
                display={identityDisplayName(identity, t)}
                onSave={next => onUpdateField('name', next)}
              />
              {hasVerifiedDocument ? (
                <Badge variant="success">
                  <CheckCircle2 className="size-3" aria-hidden />
                  {t('identity.detail.verifiedBadge')}
                </Badge>
              ) : null}
            </div>
            {identity.manuallyEditedAt ? (
              <p className="flex items-center gap-1 text-xs text-muted-foreground">
                <Pencil className="size-3" aria-hidden />
                {identity.manuallyEditedBy
                  ? t('identity.detail.editedRelativeBy', {
                      when: formatRelativeTime(identity.manuallyEditedAt) ?? '',
                      user: identity.manuallyEditedBy,
                    })
                  : t('identity.detail.editedRelative', {
                      when: formatRelativeTime(identity.manuallyEditedAt) ?? '',
                    })}
              </p>
            ) : null}
          </div>
          <div className="flex w-full flex-wrap items-center gap-2 sm:ml-auto sm:w-auto">
            {actions}
          </div>
        </div>
        <div className="mt-5 grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
          <EditableField
            label={t('identity.detail.fields.birthDate')}
            icon={Calendar}
            value={identity.birthDate}
            onSave={next => onUpdateField('birthDate', next)}
          />
          <EditableField
            label={t('identity.detail.fields.nickname')}
            icon={User}
            value={identity.nickname ?? undefined}
            onSave={next => onUpdateField('nickname', next)}
          />
          <ContactField
            label={t('identity.detail.fields.email')}
            icon={Link2}
            value={identity.email}
            linkedValues={(identity.emails ?? []).map(email => email.address)}
            onSave={next => onUpdateField('email', next)}
          />
          <ContactField
            label={t('identity.detail.fields.phone')}
            icon={Link2}
            value={identity.phone}
            linkedValues={(identity.phones ?? []).map(phone => phone.e164)}
            onSave={next => onUpdateField('phone', next)}
          />
        </div>
      </CardContent>
    </Card>
  )
}
